/**
 * Sanitize form ConfigData by removing orphaned references, ghost controls,
 * and normalizing control schemas against current definitions.
 *
 * Fixes these integrity issues:
 * 1. Orphaned references: control/row IDs in section.controls[] or row.controls[]
 *    that don't exist in controls{} or rows{}
 * 2. Ghost controls/rows: entries in controls{} or rows{} not referenced by any
 *    section or row — invisible to users but still validated/submitted
 * 3. Missing configData properties: when a control type definition evolves and
 *    adds new properties, older saved controls won't have them. This causes
 *    Vue 2 v-model bindings in the config panel to create non-reactive properties,
 *    making the panel appear "broken" or "missing stuff".
 *
 * NOTE: The orphan/ghost logic is duplicated in teamio-admin/admin.js for use in
 * bulkCopyForms. Schema normalization is frontend-only (requires CONTROLS import).
 *
 * @param {Object} configData - The parsed ConfigData object
 * @param {Object} [options] - Optional configuration
 * @param {boolean} [options.silent=false] - Suppress console logging
 * @param {Object} [options.controlDefinitions] - Control type definitions map (e.g. CONTROLS from controls.js).
 *   When provided, each control's properties are checked against its type definition and missing
 *   configData properties are filled in with defaults. This prevents config panel binding issues.
 * @returns {{ sanitized: Object, report: Object }}
 */
function sanitizeFormConfig(configData, options = {}) {
    if (!configData || typeof configData !== 'object') {
        return { sanitized: configData, report: { skipped: true, reason: 'Invalid or missing configData' } };
    }

    const data = JSON.parse(JSON.stringify(configData));
    const report = {
        orphanedControlIds: [],
        orphanedRowIds: [],
        ghostControlIds: [],
        ghostRowIds: [],
        fixedEmptyNames: [],
        normalizedControls: [],
    };

    const sections = data.sections || {};
    const rows = data.rows || {};
    const controls = data.controls || {};

    const referencedControlIds = new Set();
    const referencedRowIds = new Set();

    // Pass 1: Clean section.controls[] and section.rows[], track what's referenced
    for (const [sectionId, section] of Object.entries(sections)) {
        if (Array.isArray(section.controls)) {
            const valid = [];
            for (const controlId of section.controls) {
                if (controls[controlId]) {
                    valid.push(controlId);
                    referencedControlIds.add(controlId);
                } else {
                    report.orphanedControlIds.push({ controlId, location: `sections.${sectionId}.controls` });
                }
            }
            section.controls = valid;
        }

        if (Array.isArray(section.rows)) {
            const valid = [];
            for (const rowId of section.rows) {
                if (rows[rowId]) {
                    valid.push(rowId);
                    referencedRowIds.add(rowId);
                } else {
                    report.orphanedRowIds.push({ rowId, location: `sections.${sectionId}.rows` });
                }
            }
            section.rows = valid;
        }
    }

    // Pass 2: Clean row.controls[], track referenced controls from rows
    for (const [rowId, row] of Object.entries(rows)) {
        if (Array.isArray(row.controls)) {
            const valid = [];
            for (const controlId of row.controls) {
                if (controls[controlId]) {
                    valid.push(controlId);
                    referencedControlIds.add(controlId);
                } else {
                    report.orphanedControlIds.push({ controlId, location: `rows.${rowId}.controls` });
                }
            }
            row.controls = valid;
        }
    }

    // Pass 2.5: Fix controls with empty/missing names (prevents phantom "" keys in formInputData)
    for (const controlId of Object.keys(controls)) {
        const control = controls[controlId];
        if (!control.name || control.name.trim() === '') {
            control.name = control.uniqueId || controlId;
            report.fixedEmptyNames.push({ controlId, assignedName: control.name, type: control.type, label: control.label });
        }
    }

    // Pass 2.75: Normalize control schemas against current definitions
    // Fills in missing configData properties so Vue 2 v-model bindings are reactive
    if (options.controlDefinitions) {
        for (const controlId of Object.keys(controls)) {
            const control = controls[controlId];
            const def = options.controlDefinitions[control.type];
            if (def && def.configData) {
                const filled = [];
                for (const [key, defaultValue] of Object.entries(def.configData)) {
                    if (!(key in control)) {
                        // Deep clone the default to avoid shared references
                        control[key] = JSON.parse(JSON.stringify(defaultValue));
                        filled.push(key);
                    }
                }
                if (filled.length > 0) {
                    report.normalizedControls.push({ controlId, type: control.type, properties: filled });
                }
            }
        }
    }

    // Pass 3: Remove ghost controls (in controls{} but not referenced anywhere)
    for (const controlId of Object.keys(controls)) {
        if (!referencedControlIds.has(controlId)) {
            report.ghostControlIds.push({ controlId, label: controls[controlId].label || controls[controlId].name || 'unknown' });
            delete data.controls[controlId];
        }
    }

    // Pass 4: Remove ghost rows (in rows{} but not referenced by any section)
    for (const rowId of Object.keys(rows)) {
        if (!referencedRowIds.has(rowId)) {
            report.ghostRowIds.push({ rowId });
            delete data.rows[rowId];
        }
    }

    if (!options.silent) {
        const total = report.orphanedControlIds.length + report.orphanedRowIds.length +
                      report.ghostControlIds.length + report.ghostRowIds.length +
                      report.fixedEmptyNames.length + report.normalizedControls.length;
        if (total > 0) {
            console.log(`[sanitizeFormConfig] Cleaned ${total} issues:`, {
                orphanedControlIds: report.orphanedControlIds.length,
                orphanedRowIds: report.orphanedRowIds.length,
                ghostControls: report.ghostControlIds.length,
                ghostRows: report.ghostRowIds.length,
                fixedEmptyNames: report.fixedEmptyNames.length,
                normalizedControls: report.normalizedControls.length,
            });
            if (report.ghostControlIds.length > 0) {
                console.log('[sanitizeFormConfig] Removed ghost controls:',
                    report.ghostControlIds.map(g => `${g.controlId} (${g.label})`));
            }
            if (report.fixedEmptyNames.length > 0) {
                console.log('[sanitizeFormConfig] Fixed empty control names:',
                    report.fixedEmptyNames.map(f => `${f.controlId} -> "${f.assignedName}" (${f.type})`));
            }
            if (report.normalizedControls.length > 0) {
                console.log('[sanitizeFormConfig] Normalized control schemas:',
                    report.normalizedControls.map(n => `${n.controlId} (${n.type}): +${n.properties.join(', +')}`));
            }
        }
    }

    return { sanitized: data, report };
}

export { sanitizeFormConfig };
